import {
  loadNostoPlacements,
  nostoHasProducts,
  bindNostoNativeAddToCart,
  fixNostoSizeLabels,
  isExcludedRecommendationHandle,
  observeNostoExclusions,
  observeNostoPriceFormatting,
} from '@theme/nosto-recommendations';
import { sectionRenderer } from '@theme/section-renderer';
import { CartAddEvent } from '@theme/events';

/**
 * Nosto — active load for Complete the Look, the recommendation tabs (You May Also Like,
 * and Bestsellers when it has a placement) and the search page's featured items, plus the
 * carousel chrome (arrows / empty state) around each. Nosto renders its own native template
 * into each placement — this only triggers that render and wires the chrome around it. The
 * exception is the recommendation tabs, whose picks are re-drawn with our own product card
 * (renderNostoCards).
 */

/**
 * Roots that own a Nosto placement and its chrome. data-pdp-nosto-recs predates the
 * neutral name and is kept for Complete the Look; data-nosto-recs is the one to use for
 * anything new, since these rows are no longer PDP-only.
 */
const RECS_ROOT_SELECTOR = '[data-pdp-nosto-recs], [data-nosto-recs]';

/** @type {WeakSet<Element>} */
const initializedRoots = new WeakSet();

/** @type {ReturnType<typeof setTimeout> | null} */
let loadTimer = null;

/** @type {ReturnType<typeof setTimeout> | null} */
let settleTimer = null;

/**
 * Tab placements already observed. Not a data attribute, which a swatch morph would reset.
 * @type {WeakSet<Element>}
 */
const observedTabPlacements = new WeakSet();

/**
 * @param {HTMLElement} recsRoot
 * @param {Element} nostoContainer
 */
function syncVisibility(recsRoot, nostoContainer) {
  const hasContent = nostoHasProducts(nostoContainer);

  if (hasContent) {
    recsRoot.removeAttribute('data-nosto-empty');
    recsRoot.setAttribute('aria-hidden', 'false');
  } else {
    recsRoot.setAttribute('data-nosto-empty', '');
    recsRoot.setAttribute('aria-hidden', 'true');
  }
}

/**
 * Upper bound on how many of Nosto's picks become cards - it keeps the `handle:` search
 * query short, and is well past what a campaign returns for this row.
 */
const MAX_NOSTO_CARDS = 12;

/**
 * @param {string} path
 * @returns {string} the product handle in a `/products/<handle>` path, or ''
 */
function handleFromPath(path) {
  const match = path.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]).toLowerCase() : '';
}

/**
 * The products Nosto recommended, in its order, as handles. Read from the tile links
 * rather than from any class, because each Nosto account's template names its tiles
 * differently (docs/nosto-placements.md); a tile has several links to its product.
 * A panel marked `data-nosto-exclude-current` (Recently viewed, whose Nosto browsing
 * history can include the page's own product) drops the product being viewed.
 * @param {HTMLElement} nostoEl
 * @param {HTMLElement} panel
 * @returns {string[]}
 */
function nostoProductHandles(nostoEl, panel) {
  const excluded = panel.hasAttribute('data-nosto-exclude-current') ? handleFromPath(window.location.pathname) : '';
  /** @type {Set<string>} */
  const handles = new Set();
  for (const link of nostoEl.querySelectorAll('a[href*="/products/"]')) {
    let handle = '';
    try {
      handle = handleFromPath(new URL(link.getAttribute('href') || '', window.location.origin).pathname);
    } catch {
      continue;
    }
    // Tile pruning can miss a template with unfamiliar wrapper classes.
    if (handle && handle !== excluded && !isExcludedRecommendationHandle(handle)) handles.add(handle);
    if (handles.size >= MAX_NOSTO_CARDS) break;
  }
  return Array.from(handles);
}

/**
 * The campaign reference Nosto stamps on its own placement element
 * (`data-nosto-ref='{"ref":"productpage-nosto-2"}'`) - what its click and add-to-cart
 * attribution report against.
 * @param {HTMLElement} nostoEl
 * @returns {string}
 */
function nostoCampaignRef(nostoEl) {
  try {
    return JSON.parse(nostoEl.dataset.nostoRef || '{}').ref || nostoEl.id;
  } catch {
    return nostoEl.id;
  }
}

/**
 * Credits clicks and adds on our cards to the Nosto campaign that picked them, the way
 * Nosto credits its own tiles. Clicks: the `data-nosto-ref` marker is all Nosto's own
 * `attributeProductClicksInCampaign` writes - its document-level click listener then
 * attributes any product link inside the container - so it is set directly rather than
 * waiting on the API. Adds are reported per product; only the desktop inline size row adds
 * from inside the card - the mobile "+" adds from the body-level quick-add dialog, which
 * this listener cannot see.
 * @param {HTMLElement} cards
 * @param {string} ref
 */
function attributeCardsToNosto(cards, ref) {
  cards.dataset.nostoRef = JSON.stringify({ ref });

  if (cards.dataset.nostoCartBound === 'true') return;
  cards.dataset.nostoCartBound = 'true';
  cards.addEventListener(CartAddEvent.eventName, (event) => {
    if (!(event instanceof CartAddEvent) || event.detail.data?.didError) return;
    const productId = event.detail.data?.productId;
    if (!productId || typeof window.nostojs !== 'function') return;
    window.nostojs((api) => {
      if (typeof api.recommendedProductAddedToCart !== 'function') return;
      Promise.resolve()
        .then(() => api.recommendedProductAddedToCart(String(productId), ref))
        .catch(() => {});
    });
  });
}

/**
 * Shopify search stamps its result links with its own click-tracking params. These cards
 * are Nosto's picks, not search results, so a click on one must not be logged as a search
 * click in Shopify's search analytics.
 * @param {Element} root
 */
function stripSearchTracking(root) {
  for (const link of root.querySelectorAll('a[href*="_sid="], a[href*="_pos="], a[href*="_ss="]')) {
    const url = new URL(link.getAttribute('href') || '', window.location.origin);
    ['_pos', '_sid', '_ss'].forEach((param) => url.searchParams.delete(param));
    link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
  }
}

/**
 * Renders Nosto's picks with our own product card: this section re-rendered on the search
 * route for exactly those handles (sections/product-recommendations.liquid), the same way
 * the Recently viewed tab fetches its cards. Resolves false when nothing usable came back,
 * so the caller can fall back to Nosto's own template.
 * @param {HTMLElement} panel
 * @param {HTMLElement} nostoEl
 * @param {string[]} handles
 * @returns {Promise<boolean>}
 */
async function renderNostoCards(panel, nostoEl, handles) {
  const cards = panel.querySelector('[data-recs-nosto-cards]');
  const sectionId = panel.closest('product-recommendations-tabs')?.getAttribute('data-section-id');
  if (!(cards instanceof HTMLElement) || !sectionId) return false;

  try {
    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', handles.map((handle) => `handle:${handle}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('resources[type]', 'product');

    const html = await sectionRenderer.getSectionHTML(sectionId, true, url);
    const rendered = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(`#${panel.id} [data-recs-nosto-cards]`);
    if (!rendered?.querySelector('.resource-list__item')) return false;

    stripSearchTracking(rendered);
    cards.innerHTML = rendered.innerHTML;
  } catch {
    return false;
  }

  attributeCardsToNosto(cards, nostoCampaignRef(nostoEl));
  return true;
}

/**
 * Which list a recommendation tab with a Nosto placement shows. While Nosto is empty, or
 * its picks are still being turned into cards, the tab's own fallback stays up
 * (`data-nosto-empty`) - Shopify related products for You May Also Like, the bestsellers
 * collection for Bestsellers - so the row neither sits empty nor flashes Nosto's own
 * template first. Once the cards land the panel is `data-nosto-filled`. If the cards
 * request fails, YMAL shows Nosto's own tiles (`data-nosto-native`) as a last resort.
 *
 * A panel marked `data-nosto-no-native` (Bestsellers, Recently viewed) never shows Nosto's
 * own tiles, and starts `data-nosto-pending`: its fallback is held back, invisible, until
 * Nosto has answered (settleNostoPendingPanels). Our cards replace it whenever they arrive -
 * also after the timeout has already revealed the fallback, since Nosto's picks are the
 * list this tab is for and a slow answer is still the right one.
 * @param {HTMLElement} panel
 */
function syncNostoTabPanel(panel) {
  const nostoEl = panel.querySelector('[data-recs-nosto]');
  if (!(nostoEl instanceof HTMLElement)) return;

  const handles = nostoHasProducts(nostoEl) ? nostoProductHandles(nostoEl, panel) : [];
  const key = handles.join(' ');

  if (key !== (panel.dataset.nostoCardsKey || '')) {
    panel.dataset.nostoCardsKey = key;
    panel.removeAttribute('data-nosto-native');
    panel.removeAttribute('data-nosto-filled');
    panel.setAttribute('data-nosto-empty', '');
    panel.querySelector('[data-recs-nosto-cards]')?.replaceChildren();

    if (handles.length) {
      renderNostoCards(panel, nostoEl, handles).then((rendered) => {
        // Nosto re-rendered while this was in flight - the newer call owns the panel.
        if (panel.dataset.nostoCardsKey !== key) return;
        if (panel.hasAttribute('data-nosto-no-native')) {
          panel.removeAttribute('data-nosto-pending');
          if (!rendered) {
            announceTabContent();
            return;
          }
        }
        panel.removeAttribute('data-nosto-empty');
        panel.setAttribute('data-nosto-filled', '');
        panel.toggleAttribute('data-nosto-native', !rendered);
        nostoEl.inert = rendered;
        announceTabContent();
      });
    }
  }

  // Nosto's tiles stay in the page (invisible, see base.css) unless they are the list on
  // show, so keep their links out of the tab order and the accessibility tree.
  nostoEl.inert = !panel.hasAttribute('data-nosto-native');
  announceTabContent();
}

// Which list a recommendation tab is showing has just changed (or its tiles have), so the tabs
// section needs to re-measure its "Load more" against the new one.
function announceTabContent() {
  document.dispatchEvent(new CustomEvent('product-recommendations-tabs:content', { bubbles: true }));
}

/**
 * @param {HTMLElement} recsRoot
 */
function initCarouselUi(recsRoot) {
  if (!recsRoot || initializedRoots.has(recsRoot)) return;

  const nostoContainer = recsRoot.querySelector('.nosto_element');
  if (!nostoContainer) return;

  initializedRoots.add(recsRoot);
  /* Before the onInject observer below, so arrows and visibility measure the filtered set. */
  observeNostoExclusions(nostoContainer);
  observeNostoPriceFormatting(nostoContainer);
  bindNostoNativeAddToCart(nostoContainer);

  const prevBtn = recsRoot.querySelector('[data-nosto-scroll="prev"]');
  const nextBtn = recsRoot.querySelector('[data-nosto-scroll="next"]');

  /** @type {Element | null} */
  let boundScrollEl = null;

  const scrollTarget = () => nostoContainer.querySelector('.swiper-wrapper') || nostoContainer;

  const scrollByCard = (direction) => {
    const target = scrollTarget();
    const slide = target.querySelector('.swiper-slide, .product-tile, [class*="product"]');
    const gap = parseFloat(getComputedStyle(target).gap) || 0;
    const amount = slide ? slide.getBoundingClientRect().width + gap : target.clientWidth;
    target.scrollBy({ left: direction === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  const updateArrows = () => {
    const target = scrollTarget();
    if (prevBtn) prevBtn.disabled = target.scrollLeft <= 1;
    if (nextBtn) {
      const max = target.scrollWidth - target.clientWidth;
      nextBtn.disabled = max <= 1 || target.scrollLeft >= max - 1;
    }
  };

  const bindScroll = () => {
    const target = scrollTarget();
    if (boundScrollEl === target) return;
    if (boundScrollEl) boundScrollEl.removeEventListener('scroll', updateArrows);
    boundScrollEl = target;
    boundScrollEl.addEventListener('scroll', updateArrows, { passive: true });
  };

  prevBtn?.addEventListener('click', () => scrollByCard('prev'));
  nextBtn?.addEventListener('click', () => scrollByCard('next'));

  const onInject = () => {
    bindScroll();
    updateArrows();
    syncVisibility(recsRoot, nostoContainer);
    fixNostoSizeLabels(nostoContainer);
  };

  new MutationObserver(onInject).observe(nostoContainer, { childList: true, subtree: true });
  onInject();
}

/**
 * @returns {string[]}
 */
function getPlacementIds() {
  /** @type {Set<string>} */
  const ids = new Set();

  document.querySelectorAll(RECS_ROOT_SELECTOR).forEach((root) => {
    const id = root.getAttribute('data-nosto-placement')?.trim();
    if (id) ids.add(id);
  });

  document.querySelectorAll('[data-nosto-tab-placement]').forEach((el) => {
    const id = el.getAttribute('data-nosto-tab-placement')?.trim();
    if (id) ids.add(id);
  });

  document.querySelectorAll('.nosto_element[id^="productpage-nosto-"]').forEach((el) => {
    if (el.id) ids.add(el.id);
  });

  return Array.from(ids);
}

/**
 * Nosto page type for the current page: 'category' on collection pages, 'search' on the
 * search page, 'product' everywhere else (PDP). Read from the data-nosto-page-type
 * attribute rendered by whichever section owns the placements here, since a single Nosto
 * request covers every placement on the page and these templates never render together.
 * @returns {string}
 */
function getNostoPageType() {
  return document.querySelector('[data-nosto-page-type]')?.getAttribute('data-nosto-page-type') || 'product';
}

/**
 * In-flight Nosto loads by placement id. A repeat call for a placement that is already
 * loading waits on the same request instead of returning early, so "loadPlacements has
 * resolved" always means Nosto has answered - which is what lets settleNostoPendingPanels
 * treat an empty placement as a real "nothing".
 * @type {Map<string, Promise<void>>}
 */
const placementLoads = new Map();

/**
 * @param {string[]} placementIds
 * @param {{ productId?: string }} [options]
 * @returns {Promise<void>}
 */
async function loadPlacements(placementIds, options = {}) {
  if (!placementIds.length) return;

  /** @type {Promise<void>[]} */
  const pending = [];
  /** @type {Record<string, HTMLElement>} */
  const targets = {};
  for (const id of placementIds) {
    const el = document.getElementById(id);
    if (!(el instanceof HTMLElement) || nostoHasProducts(el)) continue;
    const inFlight = placementLoads.get(id);
    if (inFlight) pending.push(inFlight);
    else targets[id] = el;
  }

  const ids = Object.keys(targets);
  if (ids.length) {
    const load = loadNostoPlacements(targets, { pageType: getNostoPageType(), productId: options.productId }).finally(() => {
      ids.forEach((id) => placementLoads.delete(id));
    });
    ids.forEach((id) => placementLoads.set(id, load));
    pending.push(load);
  }

  await Promise.allSettled(pending);
}

/**
 * How long after navigation a `data-nosto-pending` panel waits for Nosto before showing its
 * fallback - for when Nosto never answers at all (script blocked, network). Its CSS has a
 * slightly longer backstop of its own (base.css), in case this module never runs either.
 */
const NOSTO_PENDING_TIMEOUT_MS = 5000;

/**
 * Reveals the fallback of every pending panel that has nothing on the way - Nosto answered
 * with no products, or never answered. A panel whose cards are still being fetched is left
 * alone: that fetch settles it (syncNostoTabPanel).
 */
function settleNostoPendingPanels() {
  document.querySelectorAll('[data-nosto-tab-placement][data-nosto-pending]').forEach((panel) => {
    if (!(panel instanceof HTMLElement) || panel.dataset.nostoCardsKey) return;
    panel.removeAttribute('data-nosto-pending');
    announceTabContent();
  });
}

function syncAllNostoTabPanels() {
  document.querySelectorAll('[data-nosto-tab-placement]').forEach((panel) => {
    if (panel instanceof HTMLElement) syncNostoTabPanel(panel);
  });
}

function observeNostoTabPlacements() {
  document.querySelectorAll('[data-recs-nosto]').forEach((el) => {
    if (!(el instanceof HTMLElement) || observedTabPlacements.has(el)) return;
    observedTabPlacements.add(el);
    observeNostoExclusions(el);
    observeNostoPriceFormatting(el);
    bindNostoNativeAddToCart(el);
    const panel = el.closest('[data-nosto-tab-placement]');
    new MutationObserver(() => {
      if (panel instanceof HTMLElement) syncNostoTabPanel(panel);
      fixNostoSizeLabels(el);
    }).observe(el, { childList: true, subtree: true });
    if (panel instanceof HTMLElement) syncNostoTabPanel(panel);
    fixNostoSizeLabels(el);
  });
}

/**
 * @param {{ productId?: string }} [options]
 */
function schedulePdpNostoLoad(options = {}) {
  if (loadTimer) clearTimeout(loadTimer);
  loadTimer = setTimeout(async () => {
    loadTimer = null;
    document.querySelectorAll(RECS_ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement) initCarouselUi(root);
    });
    observeNostoTabPlacements();

    await loadPlacements(getPlacementIds(), options);

    document.querySelectorAll(RECS_ROOT_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      const el = root.querySelector('.nosto_element');
      if (el) syncVisibility(root, el);
    });
    syncAllNostoTabPanels();
    // Nosto has answered (or given up), and the sync above has just read what it rendered,
    // so anything still pending with no cards on the way had nothing.
    settleNostoPendingPanels();
  }, 50);
}

/**
 * @param {number} delay
 */
function scheduleSettle(delay) {
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(settleNostoPendingPanels, delay);
}

/** @returns {string} the product the recommendation tabs are rendered for, or '' */
function currentRecsProductId() {
  return document.querySelector('product-recommendations-tabs[data-product-id]')?.getAttribute('data-product-id') || '';
}

function boot() {
  schedulePdpNostoLoad();
  // Counted from navigation, not from this deferred module's boot, so it stays ahead of
  // the CSS backstop, which starts counting when the page first renders.
  scheduleSettle(Math.max(0, NOSTO_PENDING_TIMEOUT_MS - performance.now()));

  document.addEventListener('product-recommendations-tabs:nosto', () => {
    schedulePdpNostoLoad();
  });

  // A colour swatch for another product morphs `main`, emptying the placements and resetting
  // the panels to pending. Autoload is off, so ask Nosto again for the new product. Watched
  // on the tabs' product id rather than `variant:update`: the PDP colour swatches
  // (color-swatch-nav.js) morph `main` themselves and never dispatch that event.
  let productId = currentRecsProductId();
  const main = document.querySelector('main');
  if (!main) return;
  new MutationObserver(() => {
    const nextProductId = currentRecsProductId();
    if (!nextProductId || nextProductId === productId) return;
    productId = nextProductId;
    schedulePdpNostoLoad({ productId });
    scheduleSettle(NOSTO_PENDING_TIMEOUT_MS);
  }).observe(main, { attributes: true, attributeFilter: ['data-product-id'], subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
